import React, { useState } from 'react';
import { recipeAPI } from '../../utils/api';
import { scrapeRecipe, scrapePDFRecipe, shouldShowPDFUpload } from '../../utils/recipeScraper';
import ImageUpload from '../common/ImageUpload';
import PDFUpload from '../common/PDFUpload';
import useSwipeToClose from '../../hooks/useSwipeToClose';
import './AddRecipeForm.css';

const AddRecipeForm = ({ onClose, onRecipeAdded }) => {
  const [formType, setFormType] = useState('manual'); // 'manual', 'url', or 'pdf'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Add swipe to close functionality
  const swipeRef = useSwipeToClose(onClose, true);
  
  // Form data
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    prep_time: '',
    cook_time: '',
    servings: '',
    difficulty: 'easy',
    ingredients: [{ name: '', amount: '' }],
    instructions: [''],
    tags: '',
    image_url: '',
    source_url: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageUpload = (imageUrl) => {
    setFormData(prev => ({
      ...prev,
      image_url: imageUrl || ''
    }));
  };

  const handleIngredientChange = (index, field, value) => {
    const newIngredients = [...formData.ingredients];
    newIngredients[index][field] = value;
    setFormData(prev => ({
      ...prev,
      ingredients: newIngredients
    }));
  };

  const addIngredient = () => {
    setFormData(prev => ({
      ...prev,
      ingredients: [...prev.ingredients, { name: '', amount: '' }]
    }));
  };

  const removeIngredient = (index) => {
    if (formData.ingredients.length > 1) {
      const newIngredients = formData.ingredients.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        ingredients: newIngredients
      }));
    }
  };

  const handleInstructionChange = (index, value) => {
    const newInstructions = [...formData.instructions];
    newInstructions[index] = value;
    setFormData(prev => ({
      ...prev,
      instructions: newInstructions
    }));
  };

  const addInstruction = () => {
    setFormData(prev => ({
      ...prev,
      instructions: [...prev.instructions, '']
    }));
  };

  const removeInstruction = (index) => {
    if (formData.instructions.length > 1) {
      const newInstructions = formData.instructions.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        instructions: newInstructions
      }));
    }
  };

  const handleUrlScrape = async () => {
    if (!formData.source_url) {
      setError('Please enter a URL to scrape');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const scrapedData = await scrapeRecipe(formData.source_url);
      
      setFormData(prev => ({
        ...prev,
        title: scrapedData.title || prev.title,
        description: scrapedData.description || prev.description,
        prep_time: scrapedData.prep_time || prev.prep_time,
        cook_time: scrapedData.cook_time || prev.cook_time,
        servings: scrapedData.servings || prev.servings,
        ingredients: scrapedData.ingredients || prev.ingredients,
        instructions: scrapedData.instructions || prev.instructions,
        image_url: scrapedData.image_url || prev.image_url,
        tags: scrapedData.tags ? scrapedData.tags.join(', ') : prev.tags
      }));
      
      // Switch to manual form to show the extracted data
      setFormType('manual');
      
    } catch (err) {
      console.log('Scraping error:', err.message);
      
      if (shouldShowPDFUpload(err)) {
        setError('Website blocks automated access. Please try the PDF upload tab instead.');
      } else {
        setError('Failed to scrape recipe. Please try manual entry or PDF upload.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePDFUpload = async (pdfFile) => {
    try {
      setLoading(true);
      setError('');
      
      const scrapedData = await scrapePDFRecipe(pdfFile);
      
      setFormData(prev => ({
        ...prev,
        title: scrapedData.title || prev.title,
        description: scrapedData.description || prev.description,
        prep_time: scrapedData.prep_time || prev.prep_time,
        cook_time: scrapedData.cook_time || prev.cook_time,
        servings: scrapedData.servings || prev.servings,
        ingredients: scrapedData.ingredients || prev.ingredients,
        instructions: scrapedData.instructions || prev.instructions,
        image_url: scrapedData.image_url || prev.image_url,
        tags: scrapedData.tags ? scrapedData.tags.join(', ') : prev.tags
      }));
      
      // Switch to manual form to show the extracted data
      setFormType('manual');
      
    } catch (err) {
      setError('Failed to process PDF. Please check the file and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePDFCancel = () => {
    setFormType('manual');
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      setError('Recipe title is required');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const recipeData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        prep_time: formData.prep_time ? parseInt(formData.prep_time) : null,
        cook_time: formData.cook_time ? parseInt(formData.cook_time) : null,
        servings: formData.servings ? parseInt(formData.servings) : null,
        difficulty: formData.difficulty,
        ingredients: formData.ingredients.filter(ing => ing.name.trim()),
        instructions: formData.instructions.filter(inst => inst.trim()),
        tags: formData.tags ? formData.tags.split(',').map(tag => tag.trim()).filter(Boolean) : [],
        image_url: formData.image_url.trim() || null,
        source_url: formData.source_url.trim() || null
      };

      const newRecipe = await recipeAPI.create(recipeData);
      onRecipeAdded(newRecipe);
      onClose();
      
    } catch (err) {
      setError('Failed to save recipe. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={(e) => {
      // Only close if clicking directly on overlay, not when returning from file picker
      if (e.target.className === 'modal-overlay') {
        onClose();
      }
    }}>
      <div className="modal-content" onClick={e => e.stopPropagation()} ref={swipeRef}>
        <div className="modal-header">
          <h2>Add New Recipe</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="form-type-toggle">
          <button 
            className={formType === 'manual' ? 'active' : ''}
            onClick={() => setFormType('manual')}
          >
            Manual Entry
          </button>
          <button 
            className={formType === 'url' ? 'active' : ''}
            onClick={() => setFormType('url')}
          >
            From URL
          </button>
          <button 
            className={formType === 'pdf' ? 'active' : ''}
            onClick={() => setFormType('pdf')}
          >
            From PDF
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} className="recipe-form">
          {formType === 'url' && (
            <div className="url-section">
              <div className="form-group">
                <label>Recipe URL</label>
                <div className="url-input-group">
                  <input
                    type="url"
                    name="source_url"
                    value={formData.source_url}
                    onChange={handleInputChange}
                    placeholder="https://example.com/recipe"
                  />
                  <button 
                    type="button" 
                    onClick={handleUrlScrape}
                    disabled={loading}
                    className="scrape-button"
                  >
                    {loading ? 'Scraping...' : 'Scrape'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {formType === 'pdf' && (
            <PDFUpload
              onUpload={handlePDFUpload}
              onCancel={handlePDFCancel}
              isProcessing={loading}
              type="recipe"
            />
          )}

          {formType === 'manual' && (
            <>
          <div className="form-group">
            <label>Recipe Title *</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows="3"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Prep Time (minutes)</label>
              <input
                type="number"
                name="prep_time"
                value={formData.prep_time}
                onChange={handleInputChange}
                min="0"
              />
            </div>
            <div className="form-group">
              <label>Cook Time (minutes)</label>
              <input
                type="number"
                name="cook_time"
                value={formData.cook_time}
                onChange={handleInputChange}
                min="0"
              />
            </div>
            <div className="form-group">
              <label>Servings</label>
              <input
                type="number"
                name="servings"
                value={formData.servings}
                onChange={handleInputChange}
                min="1"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Difficulty</label>
            <select
              name="difficulty"
              value={formData.difficulty}
              onChange={handleInputChange}
            >
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
            </select>
          </div>

          <div className="form-group">
            <label>Ingredients</label>
            {formData.ingredients.map((ingredient, index) => (
              <div key={index} className="ingredient-row">
                <input
                  type="text"
                  placeholder="Amount (e.g., 1 cup)"
                  value={ingredient.amount}
                  onChange={(e) => handleIngredientChange(index, 'amount', e.target.value)}
                />
                <input
                  type="text"
                  placeholder="Ingredient name"
                  value={ingredient.name}
                  onChange={(e) => handleIngredientChange(index, 'name', e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => removeIngredient(index)}
                  className="remove-button"
                >
                  ×
                </button>
              </div>
            ))}
            <button type="button" onClick={addIngredient} className="add-button">
              + Add Ingredient
            </button>
          </div>

          <div className="form-group">
            <label>Instructions</label>
            {formData.instructions.map((instruction, index) => (
              <div key={index} className="instruction-row">
                <span className="step-number">{index + 1}.</span>
                <textarea
                  placeholder="Instruction step"
                  value={instruction}
                  onChange={(e) => handleInstructionChange(index, e.target.value)}
                  rows="2"
                />
                <button
                  type="button"
                  onClick={() => removeInstruction(index)}
                  className="remove-button"
                >
                  ×
                </button>
              </div>
            ))}
            <button type="button" onClick={addInstruction} className="add-button">
              + Add Step
            </button>
          </div>

          <div className="form-group">
            <label>Tags (comma separated)</label>
            <input
              type="text"
              name="tags"
              value={formData.tags}
              onChange={handleInputChange}
              placeholder="Italian, Pasta, Quick"
            />
          </div>

          <div className="form-group">
            <label>Recipe Image</label>
            <ImageUpload
              currentImage={formData.image_url}
              onImageUpload={handleImageUpload}
            />
          </div>

          <div className="form-actions">
            <button type="button" onClick={onClose} className="cancel-button">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="submit-button">
              {loading ? 'Saving...' : 'Save Recipe'}
            </button>
          </div>
          </>
          )}
        </form>
      </div>
    </div>
  );
};

export default AddRecipeForm;